<template>
	<div
		:data-disabled="disabled"
		:inert="disabled"
		:data-loading="loading"
		:data-has-body-padding="!!bodyPadding"
		:data-accented="!!accentStyle"
		:style="accentStyle"
		class="card"
		:class="cardClass"
		v-tts="{ enabled: enableTts }"
		:data-tts-theme="ttsTheme">
		<template v-if="$slots['keyboard-shortcuts']">
			<div class="keyboard-shortcuts">
				<slot name="keyboard-shortcuts"></slot>
			</div>
		</template>
		<template v-if="title || $slots.actions">
			<div :data-size-title="sizeTitle" class="card-header">
				<div class="left" v-tts.fit.center="enableTtsTitle">
					<component :is="headingLevel" class="card-title">
						<template v-if="icon">
							<loading-indicator
								v-show="loading"
								size="xs">
							</loading-indicator>
							<template v-if="!loading">
								<span class="icon">
									<cfl-icon :icon="icon"></cfl-icon>
								</span>
							</template>
						</template>
						<template v-else>
							<loading-indicator
								v-show="loading"
								size="xs">
							</loading-indicator>
						</template>
						<span class="text" v-html="title"></span>
					</component>
					<template v-if="$slots.subtitle">
						<div class="subtitle subtitle-slot">
							<slot name="subtitle"></slot>
						</div>
					</template>
					<template v-else-if="subtitle">
						<h3 class="subtitle" v-html="subtitle"></h3>
					</template>
					<template v-if="badge">
						<div class="badge"></div>
					</template>
				</div>
				<template v-if="$slots.actions">
					<div class="right">
						<slot name="actions"></slot>
					</div>
				</template>
			</div>
		</template>
		<template v-if="$slots.header">
			<div class="card-header">
				<slot name="header"></slot>
			</div>
		</template>
		<template v-if="cover">
			<img
				class="card-cover"
				:src="cover"
				:title="coverTitle || 'Couverture d\'illustration'"
				:alt="coverTitle || 'Couverture d\'illustration'"/>
		</template>
		<template v-if="$slots.default">
			<div class="card-body">
				<slot></slot>
			</div>
		</template>
		<template v-if="$slots.footer">
			<div class="card-footer">
				<slot name="footer"></slot>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
	import IconType from "@shared-types/icon.type";
	import LoadingIndicator from "@shared-components/loading/loading-indicator.vue";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import { resolveTailwindHex } from "@shared-utils/accessibility/tailwind-palette";
	import { resolveCflHex } from "@shared-utils/colors.utils";
	import { computed, Ref, Slots, useSlots } from "vue";

	const props = withDefaults(defineProps<{
		title?: string | undefined | null,
		subtitle?: string | undefined | null,
		icon?: IconType,
		disabled?: boolean,
		loading?: boolean | Ref<boolean>,
		sizeTitle?: "sm" | "md",
		cover?: string,
		coverTitle?: string,
		enableTts?: boolean,
		enableTtsTitle?: boolean,
		ttsTheme?: string,
		headingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
		cardClass?: string,
		bodyPadding?: boolean,
		badge?: boolean,
		accentColorLight?: string,
		accentColorDark?: string,
	}>(), {
		sizeTitle: "md",
		enableTts: true,
		enableTtsTitle: false,
		headingLevel: "h2",
		bodyPadding: true,
		badge: false
	});

	const $slots = useSlots() as Slots;

	const accentStyle = computed<Record<string, string> | undefined>(() =>
	{
		if (! props.accentColorLight)
		{
			return undefined;
		}
		const light = resolveTailwindHex(props.accentColorLight) ?? resolveCflHex(props.accentColorLight);
		if (! light)
		{
			return undefined;
		}
		const darkSrc = props.accentColorDark ?? props.accentColorLight;
		const dark = resolveTailwindHex(darkSrc) ?? resolveCflHex(darkSrc, ! props.accentColorDark) ?? light;

		return { "--accent": light, "--accent-dark": dark };
	});
</script>

<style scoped>
	@reference "@main-css";

	.card {
		--card-header-px: 16px;
		--card-header-py: 16px;
		--card-header-px-md: 24px;
		--card-header-py-md: 24px;
		--card-title-leading: 24px;
		--card-title-text-leading: 30px;
		--card-title-text-leading-extra: 0px;
		--card-title-text-min-h: 24px;
		--card-icon-size: 20px;
		--card-icon-size-md: 24px;
		--card-icon-offset-y: 2px;
		--card-subtitle-size: 16px;
		--card-subtitle-opacity: 0.8;
		--card-left-gap: 12px;
		--card-body-p: 18px;
		--card-body-p-md: 24px;
		--card-footer-p: 18px;
		--card-cover-h: 120px;
		--card-cover-h-lg: 160px;
		--card-cover-h-xl: 200px;
		--card-cover-h-2xl: 240px;
		--card-disabled-opacity: 0.6;

		@variant accessibility-font-size-150 {
			--card-left-gap: 16px;
			--card-title-text-leading: 36px;
			--card-subtitle-size: 19px;
			--card-icon-size: 22px;
			--card-icon-size-md: 26px;
		}

		@variant accessibility-font-size-200 {
			--card-left-gap: 18px;
			--card-title-text-leading: 42px;
			--card-subtitle-size: 22px;
			--card-icon-size: 24px;
			--card-icon-size-md: 30px;
		}

		@variant accessibility-font-family-open-dyslexic {
			--card-title-text-leading-extra: 4px;

			@variant accessibility-font-size-150 {
				--card-title-text-leading-extra: 4px;
			}

			@variant accessibility-font-size-200 {
				--card-title-text-leading-extra: 4px;
			}
		}

		@apply relative flex flex-col rounded-(--color-card-rounded) card-shadow;
		@apply bg-(--color-card-bg) dark:bg-(--color-card-bg-alt);
		@apply text-(--color-card-text) dark:text-(--color-card-text-alt);
		@apply dark:ring-1 dark:ring-(--color-card-border-alt);
		@apply accessibility-hover-on-focus;
		@apply accessibility-color-high-contrast:ring accessibility-color-high-contrast:ring-black;
		@apply dark:accessibility-color-high-contrast:ring-white;

		/* Accent couleur : bord gauche + fond teinté + titre/icône colorés, piloté par --accent/--accent-dark
		   (props accentColor*). */

		&[data-accented="true"] {
			@apply border-l-4;
			border-left-color: var(--accent);
			background: rgb(from var(--accent) r g b / 0.06);

			.card-header .card-title {
				color: var(--accent);
			}

			@variant dark {
				border-left-color: var(--accent-dark);
				background: rgb(from var(--accent-dark) r g b / 0.1);

				.card-header .card-title {
					color: var(--accent-dark);
				}
			}
		}

		.keyboard-shortcuts {
			@apply absolute -top-3 right-3;
			@apply flex flex-row flex-nowrap gap-2;
		}

		&[data-disabled="true"] {
			@apply cursor-not-allowed;

			> div {
				@apply form-input-disabled;
			}
		}

		&[data-loading="true"] {
			@apply opacity-(--card-disabled-opacity) cursor-default pointer-events-none select-none;
		}

		.card-header {
			@apply bg-(--color-card-header-bg) dark:bg-(--color-card-header-bg-alt);
			@apply border-b-0 rounded-(--color-card-rounded);
			@apply flex flex-row justify-between items-start;
			@apply border-(--color-card-border) dark:border-(--color-card-border-alt);
			@apply px-(--card-header-px) py-(--card-header-py);
			@apply md:px-(--card-header-px-md) md:py-(--card-header-py-md);

			&[data-size-title="sm"] {
				--card-icon-offset-y: 5px;
			}

			.left {
				@apply flex flex-col items-start justify-center self-center grow shrink gap-(--card-left-gap);

				.card-title {
					@apply flex flex-row font-title items-baseline p-0 m-0;
					@apply md:text-[1.65rem];
					@apply leading-(--card-title-leading) gap-(--card-left-gap);

					.icon {
						@apply flex items-center justify-center shrink-0 translate-y-(--card-icon-offset-y);

						[data-icon] {
							@apply w-(--card-icon-size) h-(--card-icon-size);
							@apply md:w-(--card-icon-size-md) md:h-(--card-icon-size-md);
						}

						img {
							@apply w-full h-full object-contain;
						}

						@variant dark {
							filter: none;
							@apply opacity-90 contrast-75;
						}
					}

					> .text {
						line-height: calc(var(--card-title-text-leading) + var(--card-title-text-leading-extra));
						@apply min-h-(--card-title-text-min-h);

						@variant accessibility-syllables {
							@variant accessibility-font-family-open-dyslexic {
								line-height: calc(var(--card-title-text-leading) + var(--card-title-text-leading-extra) + 12px);
							}
						}

						@variant accessibility-font-size-150 {
							@variant accessibility-font-size-200 {
								@variant accessibility-font-family-open-dyslexic {
									@apply hyphens-auto;
								}
							}
						}
					}

					&:deep(.loader) {
						@apply border-vert-de-hooker dark:border-gray-4 translate-y-[2px];
					}
				}

				h3.subtitle {
					@apply italic text-(length:--card-subtitle-size) opacity-(--card-subtitle-opacity) m-0;
				}

				.subtitle-slot {
					@apply flex flex-row flex-wrap items-center gap-1.5 mt-1;

					/* Slot fourni mais sans contenu (ex. aucun filtre) → pas d'espace parasite. */

					&:empty {
						@apply hidden;
					}
				}

				.badge {
					@apply badge-pill absolute top-[-4px] left-[-4px];
				}
			}

			.right {
				@apply flex items-center shrink-0;

				&:has(.action-wrapper) {
					@apply -me-[8px];
				}

				&:deep(.action-wrapper) {
					@apply -mt-[6px] -mb-[8px];
				}
			}

			&:has(+ .card-body:not(:empty)) {
				@apply border-b rounded-b-none;

				/* En mode contraste élevé / noir-et-blanc : header & body partagent un bg trop proche,
				 * la bordure pâle (--color-card-border) ne sépare plus visuellement les 2 zones.
				 * On force noir / blanc pour rétablir la séparation. */
				@variant accessibility-color-high-contrast {
					@apply border-black dark:border-white;
				}

				@variant accessibility-color-black-and-white {
					@apply border-black dark:border-white;
				}
			}

			&:has(+ .card-cover) {
				@apply border-b rounded-b-none;

				@variant accessibility-color-high-contrast {
					@apply border-black dark:border-white;
				}

				@variant accessibility-color-black-and-white {
					@apply border-black dark:border-white;
				}
			}

			&[data-size-title="sm"] {
				.card-title {
					@apply text-lg md:text-xl;
				}
			}
		}


		.card-cover {
			@apply w-full object-cover h-(--card-cover-h);
			@apply lg:h-(--card-cover-h-lg);
			@apply xl:h-(--card-cover-h-xl);
			@apply 2xl:h-(--card-cover-h-2xl);

			&:last-child {
				@apply rounded-b-(--color-card-rounded);
			}
		}

		.card-body {
			@apply grow text-sm md:text-base;
			@apply empty:hidden;
			@apply border-(--color-card-border) dark:border-(--color-card-border-alt);
			@apply p-(--card-body-p) md:p-(--card-body-p-md);

			label.input {
				@apply bg-gray-2 dark:bg-dark-2;
			}

			&:deep(table) {
				tbody {
					tr {
						@apply border-gray-5 dark:border-dark-2;
					}
				}
			}

			&:deep(.line-skeleton) {
				@apply bg-gray-3 dark:bg-dark-2;
			}
		}

		&[data-has-body-padding="false"] {
			@apply overflow-hidden;

			.card-body {
				@apply p-0;
			}
		}

		.card-footer {
			@apply bg-[var(--color-card-footer-bg)] dark:bg-[var(--color-card-footer-bg-alt)];
			@apply border-(--color-card-border) dark:border-(--color-card-border-alt);
			@apply flex shrink-0 items-center justify-end flex-wrap border-t rounded-b-(--color-card-rounded);
			@apply gap p-(--card-footer-p);
			@apply empty:hidden;
		}

	}
</style>
